import { datadogLogs } from "@datadog/browser-logs";
import i18next from "@lib/i18next";
import { extractId } from "@repo/shopify/src/utils";
import qs from "query-string";
import { useCallback, useEffect, useState } from "react";
import { isMobile } from "react-device-detect";
import { useNavigate } from "react-router-dom";

import useLayout from "@hooks/reducerHooks/useLayout";
import useUser from "@hooks/reducerHooks/useUser";

import { URLS } from "@const/urls";

import { NOTIFICATIONS } from "@utils/notifications";

import type { IFetchVerify } from "@api/lamdaApi";
import { fetchUserVerify, fetchVerify } from "@api/lamdaApi";

declare global {
  interface Window {
    IMP: any;
  }
}

const useCertification = () => {
  const navigate = useNavigate();

  const { customer, updateUser, accessToken, fetchUser } = useUser();
  const { openModal, closeLoginModal, openToast, closeModal } = useLayout();

  const { imp_uid } = qs.parse(window.location.search.replace("?", ""));

  /** 주문 생성 lambda API 진행중 */
  const [isProgressing, setIsProgressing] = useState(false);

  /**
   * 다날 본인인증 상세 정보를 로드하는 람다 함수 호출
   * @param imp_uid 다날 본인 인증 성공 후 응답 값
   * @returns {Promise<any>} {phone, name, birthday, gender}
   */
  const fetchCertificationInfo = useCallback(
    async (imp_uid: string) => {
      if (isProgressing) return;

      try {
        setIsProgressing(true);
        return await fetchVerify(imp_uid);
      } catch (error: any) {
        closeLoginModal();
        openModal({
          title: "Verify Error",
          description: error?.response?.data.message ?? i18next.t("login.verificaton_error_description"),
          confirmButton: {
            label: "Okay",
            onClick: () => {
              closeModal();
            },
          },
        });

        const message = error instanceof Error ? error.message : String(error);
        datadogLogs.logger.error("Something went wrong", { message });
        throw new Error(`Something went wrong : ${message}`);
      } finally {
        setIsProgressing(false);
      }
    },
    [isProgressing, openModal, closeLoginModal],
  );

  /**
   * 다날 팝업 실행
   * @returns {Promise<unknown>} lambda 리턴 값: birthday, gender, name, phone
   */
  const onOpenCertificationPopup = async (): Promise<IFetchVerify | undefined> => {
    const IMP = window.IMP; // 생략 가능
    if (!IMP) return;

    IMP.init(import.meta.env.VITE_REACT_APP_IDENTIFICATION_CODE);
    return new Promise((resolve, reject) => {
      IMP.certification(
        {
          pg: `danal.{${import.meta.env.VITE_REACT_APP_DANAL_CPID}}`,
          merchant_uid: `mid_${new Date().getTime()}`,
          m_redirect_url: window.location.href,
          popup: false,
        },
        (res: any) => {
          if (res.success) {
            // pc 에서만 실행
            resolve(fetchCertificationInfo(res.imp_uid));
          } else {
            closeLoginModal();
            openModal({
              title: i18next.t("login.verificaton_error"),
              description: res.error_msg,
              confirmButton: {
                label: "Okay",
                onClick: () => {
                  navigate(URLS.HOME, { replace: true });
                  closeModal();
                },
              },
            });
          }
        },
      );
    });
  };

  /** 모바일 환경에서 본인 인증 후 인증 정보 fetch, shopify 유저 정보 업데이트, 로그인 처리 */
  const verifyMobile = async (uid: string) => {
    if (isMobile && uid && accessToken) {
      closeLoginModal();

      const verifyData = await fetchCertificationInfo(uid);

      if (!verifyData) return;

      const { phone, name, birthday, gender } = verifyData;

      const param = {
        phone,
        lastName: name.slice(0, 1),
        firstName: name.slice(1),
        birth: birthday,
        gender: gender === "male" ? "남" : "여",
        acceptsMarketing: customer.acceptsMarketing,
        customerToken: accessToken,
        customerId: extractId(customer.id),
      };

      const response = await fetchUserVerify(param);
      if (response) {
        // 다른 계정에 등록된 전화번호인 경우
        if (response?.email) {
          openModal({
            title: "Verification Error",
            description: response?.social
              ? i18next.t("toast.account.warn.phone_verify_alert_with_social", {
                  email: response.email,
                  social: response?.social,
                })
              : i18next.t("toast.account.warn.phone_verify_alert", {
                  email: response.email,
                }),
            confirmButton: {
              label: "Okay",
              onClick: () => {
                closeModal();
              },
            },
          });
          return;
        }
        if (response.data?.customerUpdate?.userErrors?.length) {
          response.data?.customerUpdate?.userErrors.forEach((error: any) => {
            openToast(error.message.includes("taken") ? NOTIFICATIONS.WARN_PHONE_VERIFY : NOTIFICATIONS.ERROR_DEFAULT);
          });
          closeLoginModal();
        } else {
          updateUser({ customer, accessToken, ...customer, ...param });
          await fetchUser({ variables: { customerAccessToken: accessToken } });
        }
      }
    }
  };

  useEffect(() => {
    if (imp_uid) verifyMobile(imp_uid as string);
  }, [imp_uid, accessToken]);

  return { onOpenCertificationPopup, fetchCertificationInfo, verifyMobile };
};

export default useCertification;
